import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';

export default function PaymentsTab({ user, centerData }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [period, setPeriod]             = useState('month');
  const [stats, setStats]               = useState({ total: 0, jobs: 0, avgJob: 0, cocoFee: 0, netPayout: 0 });

  useEffect(() => { if (centerData?.id) loadPayments(); else setLoading(false); }, [centerData, period]);

  const loadPayments = async () => {
    setLoading(true);
    try {
      const now   = new Date();
      const start = period === 'week'
        ? new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        : period === 'month'
        ? new Date(now.getFullYear(), now.getMonth(), 1)
        : new Date(now.getFullYear(), 0, 1);

      const q = query(
        collection(db, 'washRequests'),
        where('serviceCenterId', '==', centerData.id),
        where('centerStatus', '==', 'completed'),
        where('completedAt', '>=', Timestamp.fromDate(start)),
        orderBy('completedAt', 'desc'),
        limit(50)
      );
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTransactions(list);

      const total    = list.reduce((s, j) => s + (j.quotedCenterAmount || 0), 0);
      const feeRate  = centerData.trialActive ? 0 : (centerData.cocoWashFeePercentage || 15) / 100;
      const cocoFee  = total * feeRate;
      setStats({ total, jobs: list.length, avgJob: list.length ? total / list.length : 0, cocoFee, netPayout: total - cocoFee });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fmt = (v) => '$' + Number(v || 0).toFixed(2);

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 900 }}>Payments</h1>
          <p style={{ color: '#6b7280', marginTop: 4 }}>Revenue from CocoWash referred jobs</p>
        </div>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#fff' }}>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="year">This Year</option>
        </select>
      </div>

      {/* Trial banner */}
      {centerData?.trialActive && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 14, marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 20 }}>🎉</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#16a34a' }}>Free Trial Active — You keep 100% of every job</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>The 15% CocoWash fee only applies after your 90-day trial ends</div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Gross Revenue', value: fmt(stats.total), color: '#F97316' },
          { label: 'Jobs Completed', value: stats.jobs, color: '#3b82f6' },
          { label: 'Avg per Job', value: fmt(stats.avgJob), color: '#7c3aed' },
          { label: 'CocoWash Fee', value: centerData?.trialActive ? 'Waived' : fmt(stats.cocoFee), color: '#6b7280' },
          { label: 'Your Payout', value: fmt(stats.netPayout), color: '#16a34a' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#111827' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Transactions */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 16 }}>Transaction History</h3>
        {loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : transactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#6b7280' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>💳</div>
            <p>No completed jobs in this period</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f3f4f6' }}>
                {['Date', 'Services', 'Job #', 'Amount', 'CocoWash Fee', 'Your Payout'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.map(job => {
                const amt     = job.quotedCenterAmount || 0;
                const feeRate = centerData?.trialActive ? 0 : (centerData?.cocoWashFeePercentage || 15) / 100;
                const fee     = amt * feeRate;
                return (
                  <tr key={job.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                    <td style={{ padding: '12px', fontSize: 13 }}>{job.completedAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                    <td style={{ padding: '12px', fontSize: 13, fontWeight: 600 }}>{(job.selectedServices || []).join(', ') || '—'}</td>
                    <td style={{ padding: '12px', fontSize: 12, color: '#6b7280' }}>#{job.id.slice(-6).toUpperCase()}</td>
                    <td style={{ padding: '12px', fontSize: 13, fontWeight: 700 }}>{fmt(amt)}</td>
                    <td style={{ padding: '12px', fontSize: 13, color: centerData?.trialActive ? '#16a34a' : '#6b7280' }}>{centerData?.trialActive ? 'Waived' : fmt(fee)}</td>
                    <td style={{ padding: '12px', fontSize: 14, fontWeight: 900, color: '#16a34a' }}>{fmt(amt - fee)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Payment method note */}
      <div style={{ marginTop: 16, padding: 14, background: '#fff7ed', borderRadius: 12, border: '1px solid #fed7aa', fontSize: 13, color: '#92400e' }}>
        💳 CocoWash drivers pay using the CocoWash Issued Payment Card at your facility. Statements are generated monthly. Questions? Contact partners@cocowash.org
      </div>
    </div>
  );
}